import * as turf from "@turf/turf";
import type { FormikProps } from "formik";
import moment from "moment/min/moment-with-locales";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  generateLink,
  getPhaseOfDateRange,
  openLink,
  useAuthContext,
  useConvertCase,
  useGlobalState,
} from "senf-shared/index";
import type { Project } from "senf-shared/types";
import { getZoomLevel } from "../../atoms/map/utils/helpers";
import type { IconName } from "../../core/icon/names";
import type { QuestionValue, Result } from "./legacy/survey.types";
import {
  type InputColumn,
  type InputRow,
  type Page,
  type PageType,
  pageTypeConfig,
  type QuestionType,
} from "./survey.config";
import { getMostRecentlyViewedPageIndex } from "./utils";

export const getMapType = (page: Page<PageType>) => {
  const typeMap: Partial<Record<PageType, string>> = {
    mapPoint: "point",
    mapPath: "path",
    mapArea: "area",
  };
  return page.type in typeMap ? typeMap[page.type] : page.type;
};

type SurveyNavigationProps = {
  pages: Page[];
  index: number;
  formik?: FormikProps<Result["values"]>;
  preview?: boolean;
};

type PrepareSurveyPageProps = SurveyNavigationProps & {
  isMobile?: boolean;
  project?: Project;
};

export type ChartViews =
  | "grid"
  | "heatmap"
  | "drawn"
  | "hexabin"
  | "table"
  | "pie"
  | "bar"
  | "column"
  | "wordcloud"
  | "list";

type ChartViewBaseProps = {
  icon: IconName;
  type: "map" | "chart" | "wordcloud" | "table";
};
export type ChartViewConfig = Record<
  ChartViews,
  ChartViewBaseProps & {
    text: string;
    active: boolean;
  }
>;

export const gridTypeProps: Record<ChartViews, ChartViewBaseProps> = {
  heatmap: {
    icon: "map-heat",
    type: "map",
  },
  hexabin: {
    icon: "map-hexabin",
    type: "map",
  },
  drawn: {
    icon: "map-drawn",
    type: "map",
  },
  grid: {
    icon: "statistics-grid",
    type: "chart",
  },
  pie: {
    icon: "statistics-pie",
    type: "chart",
  },
  bar: {
    icon: "statistics-bar",
    type: "chart",
  },
  column: {
    icon: "statistics-column",
    type: "chart",
  },
  wordcloud: {
    icon: "wordcloud",
    type: "wordcloud",
  },
  table: {
    icon: "table",
    type: "table",
  },
  list: {
    icon: "list",
    type: "table",
  },
};
const storedRandomOrder: Record<string, InputRow<PageType>[]> = {};

const generate = (arr: InputRow<PageType>[]) =>
  Array.from(arr, () => Math.floor(Math.random() * 100));

const isAscending = (arr: number[]) =>
  arr.every(
    (value, index, array) =>
      index === array.length - 1 || value < array[index + 1],
  );

const randomize = (arr: InputRow<PageType>[]): InputRow<PageType>[] => {
  let order = [] as number[];
  while (isAscending(order)) order = generate(arr);

  return arr
    .map((e, i) => ({ item: e, order: order[i] }))
    .sort((a, b) => a.order - b.order)
    .map(({ item }) => item);
};

export const usePrepareSurveyPage = ({
  pages,
  index,
  isMobile,
  project,
}: PrepareSurveyPageProps) => {
  const { t } = useTranslation();
  const { toTitleCase } = useConvertCase();
  const getPageByIndex = (i = index) => {
    const currentPage = pages?.[i] as Page;
    if (!currentPage) return undefined;

    const currentPageMapData =
      currentPage?.map?.mapId && currentPage?.map?.mapId !== ""
        ? project?.maps?.find((map) => map.mapId === currentPage?.map?.mapId)
        : null;
    // const { mapDataLoading, error, mapData } = useMapData(
    //   project,
    //   currentPage?.mapId
    // );

    const outroIndex = (pages?.length || 0) - 1;

    const pageRoles = ["intro", "outro", "demographics"];

    const currentPageRole = pageRoles.includes(currentPage?.type)
      ? currentPage?.type
      : "surveyPage";

    const currentPageIsMapbased =
      currentPage?.type === "mapPoint" ||
      currentPage?.type === "mapPath" ||
      currentPage?.type === "mapArea";

    const currentPageHasMap =
      currentPageIsMapbased ||
      (currentPage?.map?.mapId && currentPage?.map?.mapId !== "");

    if ((currentPageIsMapbased || currentPageHasMap) && currentPageMapData) {
      const [longitude, latitude] = turf.center(currentPageMapData?.mapbounds)
        ?.geometry?.coordinates || [51.1657, 10.4515];
      const zoom = getZoomLevel(turf.bbox(currentPageMapData?.mapbounds));

      currentPageMapData.initialMapViewport = {
        latitude,
        longitude,
        zoom,
        duration: 0,
        pitch: 0,
        bearing: 50,
        maxZoom: 20,
        minZoom: 5,
      };
    }

    const currentPageHasQuestion =
      !pageTypeConfig[currentPage?.type]?.structuralPage;

    const currentPageImages = currentPage?.images?.items;
    const currentPageHasImage = currentPageImages?.length;

    const currentPageAlignment =
      (currentPage?.type === "intro" || currentPage?.type === "outro") &&
      ((!currentPageHasImage && !currentPageHasMap) || isMobile)
        ? ("center" as const)
        : undefined;

    const progress = Math.max(0, Math.min(i / outroIndex, 1) * 100);

    return {
      currentPage,
      currentPageRole,
      currentPageIsMapbased,
      currentPageHasMap,
      currentPageHasImage,
      currentPageHasQuestion,
      currentPageImages,
      currentPageAlignment,
      currentPageMapData,
      progress,
    };
  };

  const getViewsConfig = (currentPageIndex: number) => {
    const { currentPageIsMapbased, currentPage, currentPageRole } =
      getPageByIndex(currentPageIndex) || {};
    const isSurveyPage = currentPageRole === "surveyPage";

    const mapQuestion =
      currentPage?.type === "mapPoint" ||
      currentPage?.type === "mapPath" ||
      currentPage?.type === "mapArea";

    const hasBooleanResponse = !!(
      currentPage?.type === "choice" ||
      currentPage?.type === "choiceImage" ||
      currentPage?.type === "choiceMatrix" ||
      currentPage?.type === "budgetAllocation" ||
      currentPage?.type === "ratingScale" ||
      currentPage?.type === "ratingSlider" ||
      currentPage?.type === "ratingStars" ||
      currentPage?.type === "ranking" ||
      (mapQuestion && currentPage?.settings?.valueType === "boolean")
    );

    const hasStringyResponse = !!(
      currentPage?.type === "textResponse" ||
      currentPage?.type === "numberInput" ||
      (mapQuestion &&
        (currentPage?.settings?.valueType === "string" ||
          currentPage?.settings?.valueType === "number")) ||
      (hasBooleanResponse && currentPage?.settings?.otherOption?.enabled)
    );

    return {
      heatmap: {
        ...gridTypeProps.heatmap,
        text: toTitleCase(t("global:heatmap")),
        active: currentPageIsMapbased,
      },
      drawn: {
        ...gridTypeProps.drawn,
        text: toTitleCase(t("global:map")),
        active: currentPageIsMapbased,
      },
      hexabin: {
        ...gridTypeProps.hexabin,
        text: toTitleCase(t("global:hexabin")),
        active: currentPageIsMapbased,
      },
      grid: {
        ...gridTypeProps.grid,
        text: toTitleCase(t("global:gridChart")),
        active:
          isSurveyPage &&
          !currentPageIsMapbased &&
          currentPage?.type !== "textResponse",
      },
      pie: {
        ...gridTypeProps.pie,
        text: toTitleCase(t("global:pieChart")),
        active: isSurveyPage && (hasStringyResponse || hasBooleanResponse),
      },
      bar: {
        ...gridTypeProps.bar,
        text: toTitleCase(t("global:barChart")),
        active: isSurveyPage && (hasStringyResponse || hasBooleanResponse),
      },
      column: {
        ...gridTypeProps.column,
        text: toTitleCase(t("global:columnChart")),
        active: isSurveyPage && (hasStringyResponse || hasBooleanResponse),
      },
      wordcloud: {
        ...gridTypeProps.wordcloud,
        text: toTitleCase(t("global:wordCloud")),
        active: hasStringyResponse,
      },
      table: {
        ...gridTypeProps.table,
        text: toTitleCase(t("global:table")),
        active: hasStringyResponse,
      },
    } as ChartViewConfig;
  };

  return {
    ...getPageByIndex(),
    getPageByIndex,
    getViewsConfig,
  };
};

const getCondition = ({ pages, index, formik }: SurveyNavigationProps) => {
  const page = pages[index];
  const { value } = (page && formik?.values[page.uid]) || {};
  return (
    page?.conditions?.find(({ type, response }) => {
      if (type === "equals") {
        return (
          response === value ||
          (Array.isArray(value) && value?.includes(response || ""))
        );
      }
      if (type === "notEquals") {
        return (
          response !== value &&
          !(Array.isArray(value) && value?.includes(response || ""))
        );
      }
      return false;
    }) || page?.conditions?.[0]
  );
};

const getNextIndex = ({ pages, index, formik }: SurveyNavigationProps) => {
  const condition = getCondition({ pages, index, formik });
  const incrementByOne =
    pages[index]?.type === "intro" ||
    !condition ||
    condition.goToPage === "next";
  return incrementByOne
    ? index + 1
    : pages.findIndex((e) => e.uid === condition.goToPage);
};

const useProfileCompletedInOtherSession = () => {
  const { user } = useAuthContext();
  const key = useMemo(() => `${user?.userId}-profileCompleted`, [user?.userId]);
  const isProfileCompleted = () =>
    user && sessionStorage.getItem(key) === "true";
  useEffect(() => {
    if (user && sessionStorage.getItem(key) == null)
      sessionStorage.setItem(key, user.profileCompleted.toString());
  }, [user, key]);
  return isProfileCompleted;
};

export const useNavigationIndex = ({
  pages,
  index,
  formik,
  preview,
}: SurveyNavigationProps) => {
  const isProfileCompleted = useProfileCompletedInOtherSession();

  const profileCompleted = isProfileCompleted() && !preview;

  const { previousIndex, nextIndex } = useMemo(() => {
    let previousIndex = getMostRecentlyViewedPageIndex(
      pages,
      formik?.values,
      index,
      1,
    );
    // this is a special case when demographics is the first page and it is completed, then there is no timestamp for this page
    if (pages[previousIndex]?.type === "demographics" && profileCompleted) {
      previousIndex--;
    }
    let nextIndex = getNextIndex({ pages, index, formik });
    if (pages[nextIndex]?.type === "demographics" && profileCompleted) {
      nextIndex = getNextIndex({ pages, index: nextIndex, formik });
    }
    return {
      previousIndex,
      nextIndex,
    };
  }, [pages, index, formik, profileCompleted]);

  return {
    nextIndex,
    previousIndex,
    isLastPage: pages[nextIndex]?.type === "outro",
  };
};

/**
 * if you need to access the defaults in a loop, don't pass
 * the page to the hook. The hook will return a function
 * which can be used in the loop.
 * */
type ReturnedDefaults = {
  defaultTitle: string;
  titlePlaceholder: string;
  defaultDescription: string;
  defaultTextTitle: string;
  defaultOtherTitle: string;
  defaultMapSelectDescription: string;
};
export function useDefaultValues(): (p: Page) => ReturnedDefaults;
export function useDefaultValues(p: Page): ReturnedDefaults;
export function useDefaultValues(p?: Page) {
  const { t } = useTranslation();

  const getDefaults = useMemo(
    () => (page: Page) => {
      const defaultTitleMap: Partial<Record<PageType, string>> = {
        intro: "survey:builder.titleOfIntro",
        outro: "survey:builder.titleOfOutro",
        section: "survey:builder.titleOfYourSection",
        demographics: "global:demographicData",
      };
      const defaultTitle = defaultTitleMap[page?.type]
        ? t(defaultTitleMap[page?.type] || "")
        : undefined;

      const titlePlaceholder =
        defaultTitle || t("survey:builder.titleOfYourQuestion");

      const getFeatureType = (type: string, count: number) => {
        return t(count === 1 ? `global:${type}` : `global:${type}s`);
      };

      const getChoiceType = (count: number) => {
        return t(count === 1 ? "global:option" : "global:options");
      };

      const isChoicePage =
        page?.type === "choice" ||
        page?.type === "choiceImage" ||
        page?.type === "choiceMatrix";

      const isMapPage =
        page?.type === "mapPoint" ||
        page?.type === "mapPath" ||
        page?.type === "mapArea";

      const minMaxChoiceKey = isChoicePage
        ? !page?.settings.multipleChoice?.enabled
          ? "youCanOnlySelectOneOption"
          : page?.settings.multipleChoice.min &&
              page?.settings.multipleChoice.max &&
              page?.settings.multipleChoice.min ===
                page?.settings.multipleChoice.max
            ? "youMustSelectExactlyChoices"
            : page?.settings.multipleChoice.min &&
                page?.settings.multipleChoice.max
              ? "youMustSelectMinMaxChoices"
              : !page?.settings.multipleChoice.min &&
                  page?.settings.multipleChoice.max
                ? "youMustSelectMaxChoices"
                : page?.settings.multipleChoice.min &&
                    !page?.settings.multipleChoice.max
                  ? "youMustSelectMinChoices"
                  : "youCanSelectMultipleOptions"
        : "";

      const minMaxFeatureKey = isMapPage
        ? !page?.settings.mapResponse.limit?.enabled
          ? "youCanAddMultipleFeatures"
          : page?.settings.mapResponse.limit.min &&
              page?.settings.mapResponse.limit.max &&
              page?.settings.mapResponse.limit.min ===
                page?.settings.mapResponse.limit.max
            ? "youMustAddExactlyFeatures"
            : page?.settings.mapResponse.limit.min &&
                page?.settings.mapResponse.limit.max
              ? "youMustAddMinMaxFeatures"
              : !page?.settings.mapResponse.limit.min &&
                  page?.settings.mapResponse.limit.max
                ? "youMustAddMaxFeatures"
                : page?.settings.mapResponse.limit.min &&
                    !page?.settings.mapResponse.limit.max
                  ? "youMustAddMinFeatures"
                  : "youCanAddMultipleFeatures"
        : "";

      const choiceConfig = isChoicePage
        ? {
            count: !page?.settings.multipleChoice?.enabled
              ? 1
              : page?.settings.multipleChoice.min ||
                page?.settings.multipleChoice.max ||
                2,
            maxCount: page?.settings.multipleChoice?.max,
            choiceType: getChoiceType(
              !page?.settings.multipleChoice?.enabled
                ? 1
                : page?.settings.multipleChoice.min ||
                    page?.settings.multipleChoice.max ||
                    2,
            ),
          }
        : undefined;
      const featureConfig = isMapPage
        ? {
            count: !page?.settings.mapResponse.limit?.enabled
              ? 1
              : page?.settings.mapResponse.limit.min ||
                page?.settings.mapResponse.limit.max ||
                2,
            maxCount: page?.settings.mapResponse.limit?.max,
            featureType: getFeatureType(
              getMapType(page) || "point",
              !page?.settings.mapResponse.limit?.enabled
                ? 2
                : page?.settings.mapResponse.limit.min ||
                    page?.settings.mapResponse.limit.max ||
                    2,
            ),
          }
        : undefined;

      const defaultDescriptionMap: Partial<
        Record<
          PageType | "default",
          [string] | [string, Record<string, unknown> | undefined]
        >
      > = {
        choice: [`survey:options.${minMaxChoiceKey}`, choiceConfig],
        choiceImage: [`survey:options.${minMaxChoiceKey}`, choiceConfig],
        choiceMatrix: [`survey:options.${minMaxChoiceKey}`, choiceConfig],
        mapPoint: [`survey:options.${minMaxFeatureKey}`, featureConfig],
        mapPath: [`survey:options.${minMaxFeatureKey}`, featureConfig],
        mapArea: [`survey:options.${minMaxFeatureKey}`, featureConfig],
        ranking:
          page.type === "ranking"
            ? [
                `survey:options.${page?.settings.limit?.enabled ? "rankingDescriptionWithLimit" : "rankingDescription"}`,
                {
                  count: page?.settings.limit?.max || 1,
                },
              ]
            : undefined,
        default: ["survey:builder.titleOfYourQuestion"],
      };
      const [desc1, desc2] = defaultDescriptionMap[page?.type] ||
        defaultDescriptionMap?.default || [""];
      let defaultDescription = t(desc1, desc2);
      if (page?.type === "choiceMatrix") {
        defaultDescription += `, ${t("survey:options.perRow")}`;
      }

      const textPlaceholderMap: Partial<Record<PageType | "default", string>> =
        {
          choice: "survey:pleaseSpecify",
          ratingScale: "survey:elaborateBriefly",
          ratingStars: "survey:elaborateBriefly",
          ratingSlider: "survey:elaborateBriefly",
          mapPoint: t("util:enterDescriptionFor", {
            val: t(`global:${getMapType(page)}`),
          }),
          mapPath: t("util:enterDescriptionFor", {
            val: t(`global:${getMapType(page)}`),
          }),
          mapArea: t("util:enterDescriptionFor", {
            val: t(`global:${getMapType(page)}`),
          }),
          numberInput: "survey:pleaseEnter",
          default: "survey:describeBriefly",
        };

      // We do this so that the mapbased radio other option gets the right placeholder
      const type =
        isMapPage && page?.settings?.valueType === "boolean"
          ? "choice"
          : page?.type;

      const defaultTextTitle = t(
        textPlaceholderMap[type] || textPlaceholderMap?.default || "",
      );
      const defaultOtherTitle = t("global:other");
      const defaultMapSelectDescription = t("survey:selectOption");

      return {
        defaultTitle,
        titlePlaceholder,
        defaultDescription,
        defaultTextTitle,
        defaultOtherTitle,
        defaultMapSelectDescription,
      };
    },
    [
      t,
      p?.type,
      (p as Page<"choice">)?.settings?.multipleChoice?.enabled,
      (p as Page<"choice">)?.settings?.multipleChoice?.min,
      (p as Page<"choice">)?.settings?.multipleChoice?.max,
      (p as Page<"mapPoint">)?.settings?.valueType,
      (p as Page<"mapPoint">)?.settings?.mapResponse?.limit?.enabled,
      (p as Page<"mapPoint">)?.settings?.mapResponse?.limit?.min,
      (p as Page<"mapPoint">)?.settings?.mapResponse?.limit?.max,
      (p as Page<"ranking">)?.settings?.limit?.enabled,
    ],
  );

  return useMemo(() => (p ? getDefaults(p) : getDefaults), [getDefaults, p]);
}

export const useRandomSorting = <
  T extends QuestionType,
  K extends "columns" | "rows" = "rows",
>(
  page: Page<T>,
  key: K,
  mode: "edit" | "display" = "display",
  remember = true,
): NonNullable<Page<T>["options"]>[K] => {
  const [sortedOptions, setSortedOptions] = useState<
    (InputColumn<T> | InputRow<T>)[]
  >(page.options?.[key] || []);

  // Create a string representation of the current options order for deep comparison
  const optionsOrderKey = useMemo(() => {
    return page.options?.[key]?.map((item) => item.uid).join(",") || "";
  }, [page.options?.[key]]);

  useEffect(() => {
    if (mode === "display" && page?.settings?.shuffle && page.options?.[key]) {
      const currentOptions = page.options?.[key] || [];
      const storedOrder = storedRandomOrder[page.uid];
      const optionsChanged =
        !storedOrder || storedOrder.length !== currentOptions.length;

      if (!storedOrder || !remember || optionsChanged) {
        // Generate new random order if:
        // - Not in memory
        // - Not remembering
        // - Options have changed
        const newOrder = randomize(currentOptions);
        storedRandomOrder[page.uid] = newOrder;
        setSortedOptions(newOrder);
      } else {
        setSortedOptions(storedOrder);
      }
    } else {
      setSortedOptions(page.options?.[key] || []);
    }
  }, [mode, page, key, remember, optionsOrderKey]);

  return sortedOptions;
};

interface UseVerifyFilesProps {
  mimeTypes?: string[];
  maxSize?: number;
}
export const useVerifyFiles = ({
  mimeTypes = ["image/", "video/"], // Added "video/" to support video files
  maxSize = 20000000, // 20 MB
}: UseVerifyFilesProps = {}) => {
  const [invalidType, setInvalidType] = useState(false);
  const [invalidSize, setInvalidSize] = useState(false);

  const validType = (type: string) =>
    mimeTypes.some((mimeType) => type.startsWith(mimeType));

  const validSize = (size: number) => size < maxSize;

  const checkFileUpload = ({ files }) => {
    const validFiles = [] as File[];

    [...(files || [])].forEach((file) => {
      if (validType(file.type)) {
        if (validSize(file.size)) validFiles.push(file);
        else setInvalidSize(true);
      } else setInvalidType(true);
    });

    setTimeout(
      () => {
        setInvalidSize(false);
        setInvalidType(false);
      },
      validFiles.length ? 0 : 2000,
    );

    return validFiles;
  };

  return { invalidType, invalidSize, checkFileUpload };
};

export const useOverflow = (ref?: React.RefObject<HTMLDivElement>) => {
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const { scrollWidth, clientWidth } = ref?.current || {
    scrollWidth: 0,
    clientWidth: 0,
  };
  useEffect(() => {
    const r = ref?.current;
    const handleScroll = () => setScrollLeft(r?.scrollLeft || 0);
    r?.addEventListener("scroll", handleScroll);
    return () => r?.removeEventListener("scroll", handleScroll);
  }, [ref?.current]);

  const direction = document.documentElement.getAttribute("dir");
  const start =
    scrollWidth > clientWidth &&
    (direction === "ltr" ? scrollLeft > 5 : scrollLeft < scrollWidth - 5);
  const end =
    scrollWidth > clientWidth &&
    (direction === "ltr"
      ? scrollLeft + clientWidth < scrollWidth - 5
      : scrollLeft < 5);

  return {
    start,
    end,
  };
};

export const useSurveyImageSettings = () => {
  return {
    rowGrid: 5,
    defaultImageDimensions: {
      w: 2,
      h: 5,
      x: 0,
      y: 0,
    },
  };
};

export const useFormatDuration = () => {
  const formatDuration = (duration: number) => {
    if (duration < 1000) return "< 1s";
    return moment
      .utc(moment.duration(duration).asMilliseconds())
      .format(duration >= 3600000 ? "H[h] m[m] s[s]" : "m[m] s[s]");
  };
  return { formatDuration };
};

const getValuesForKey = <T extends object = Result["values"]>(
  array: { values: T }[],
  key: keyof T,
) => {
  return array
    .map((obj) => {
      const newObj = {} as T;
      if (obj.values?.[key]) {
        newObj[key] = obj.values[key];
      }
      return newObj;
    })
    .filter((obj) => key in obj && (obj[key]?.value || obj[key]?.geoData));
};

export const useGenerateStats = ({
  results,
  pages,
}: {
  results: Result[];
  pages: Page[];
}) => {
  const { i18n } = useTranslation();

  const pagesById = pages.reduce(
    (acc, page) => {
      acc[page.uid] = page;
      return acc;
    },
    {} as Record<string, Page>,
  );

  const completeDemographics = (result: Result) => {
    const userData = result.userData || {};
    return userData.age || userData.gender || userData.postcode;
  };

  /**
   * Returns the results that were answered for a specific question
   * @returns
   */
  const answeredQuestion = ({ type, uid }: Page, r = results || []) =>
    type === "section"
      ? r
      : type === "demographics"
        ? r.filter(completeDemographics)
        : getValuesForKey(r, uid);

  // const hasValue = (values: Result, question: Page) => {
  //   if (question.type === "demographics") return completeDemographics(values);

  //   return getValuesForKey(results, question.uid).length
  //   const r = values.values[question.uid];
  //   console.log(question?.title, r);
  //   return r?.value || r?.geoData || r?.comment;
  // };
  // const legacyBounceRate = true;
  const legacyBounceRate = !results?.some(({ values }) =>
    Object.values(values).some((e) => e.createdAt),
  );

  let paddedResults = (results || []).map((result) => {
    const intro = {
      [pages[0]?.uid]: { timeStamp: result.createdAt } as QuestionValue,
    };

    const firstPage = {
      [pages[1]?.uid]: { createdAt: result.createdAt } as QuestionValue,
    };

    const outro = {
      [pages[pages.length - 1]?.uid]: {
        timeStamp: result.createdAt,
      } as QuestionValue,
    };

    const paddedValues = {
      ...intro,
      // in case no entries were submitted, we need to set the first page as viewed
      ...firstPage,
      ...result.values,
      // in case the survey was completed, we need to set the outro
      // so that the last page is not counted as bounced
      ...(result.completed ? outro : {}),
    };

    return { ...result, values: paddedValues };
  });

  if (legacyBounceRate) {
    paddedResults = paddedResults.map((result) => {
      const entries = Object.entries(result.values);
      const [lastEntryId, lastEntryValue] =
        entries.filter(([, value]) => !!value.timeStamp).pop() || [];
      const currentPageIndex = pages.findIndex((e) => e.uid === lastEntryId);
      const nextPage = pages[currentPageIndex + 1];

      if (!nextPage) return result;

      const answeredQuestions = Object.fromEntries(
        entries.map(([uid, value]) => [
          uid,
          {
            ...value,
            ...(nextPage && value.timeStamp
              ? { createdAt: value.timeStamp }
              : {}),
          },
        ]),
      );

      return {
        ...result,
        values: {
          ...answeredQuestions,
          [nextPage.uid]: {
            value: `legacy bounced page ${nextPage.title || nextPage.type}`,
            createdAt: new Date(
              lastEntryValue?.timeStamp ?? new Date().getTime() + 100,
            ).toISOString(),
          },
        },
      };
    });
  }

  const totalsRaw = {} as Record<string, number[]>;

  const raw =
    paddedResults.reduce(
      (acc, result) => {
        const entries = Object.entries(result.values);
        entries
          .sort(
            ([, a], [, b]) =>
              new Date(a?.createdAt || a?.viewed || "").getTime() -
              new Date(b?.createdAt || b?.viewed || "").getTime(),
          )
          .forEach(([uid, value], i, arr) => {
            if (
              pagesById[uid]?.type === "intro" ||
              pagesById[uid]?.type === "outro"
            )
              return;
            if (!acc[uid])
              acc[uid] = {
                bounced: 0,
                ms: [],
              };

            if (i === arr.length - 1) {
              acc[uid].bounced = (acc[uid].bounced || 0) + 1;
            }
            if (!acc[uid].ms) acc[uid].ms = [];
            const [, nextValue] = arr[i + 1] || [];
            const ms = Math.max(
              0,
              new Date(
                nextValue?.createdAt ||
                  nextValue?.viewed ||
                  result.completed ||
                  nextValue?.timeStamp ||
                  0,
              ).getTime() -
                new Date(value.createdAt || value.viewed || 0).getTime(),
            );

            if (ms < 300000) {
              acc[uid].ms.push(ms);
              if (!totalsRaw[result.entryId]) totalsRaw[result.entryId] = [];
              totalsRaw[result.entryId].push(ms);
            }
          });
        return acc;
      },
      {} as Record<string, { bounced: number; ms: number[] }>,
    ) || {};

  const totals = Object.values(totalsRaw)
    /**
     * do not filter those out because we also want to see the average completion time if no one completed the survey
     */
    //.filter((e) => e.length > (pages.length - 3))
    .map((e) => e.reduce((acc, curr) => acc + curr, 0))
    .sort((a, b) => a - b);

  const stats = Object.fromEntries(
    Object.entries(raw).map(([pageUid, value]) => {
      const answered = answeredQuestion(pagesById[pageUid] || {});
      const ms = value.ms.sort((a, b) => a - b);
      const total = ms.reduce((acc, curr) => acc + curr, 0);
      return [
        pageUid,
        {
          ...value,
          ms,
          total,
          bounceRate: value.bounced / results.length,
          median: ms[Math.floor(ms.length / 2)],
          average: total / (answered.length || 1),
        },
      ];
    }),
  );

  const getPageStats = (index: number) => {
    const page = pages[index];
    const { bounced, median, average } = stats[page.uid] || {};
    const answeredQuestionsRate = `${
      answeredQuestion(page || {}).length || 0
    } / ${results?.length || 1}`;

    const bounceRate = new Intl.NumberFormat(i18n.resolvedLanguage, {
      style: "percent",
      maximumFractionDigits: 1,
    }).format((bounced || 0) / (results?.length || 1));
    return { bounced, median, average, bounceRate, answeredQuestionsRate };
  };
  return { stats, getPageStats, totals };
};
